import React, { useState, useCallback, useRef } from "react";
import FormStepProgress from "./FormStepProgress.jsx";
import FormStickyFooter from "./FormStickyFooter.jsx";
import { FORM_WIZARD_STEPS } from "./borrowerUploadConfig.js";
import { useBorrowerFormDraft, saveFormDraft } from "./useBorrowerFormDraft.js";

export default function BorrowerSteppedForm({
  title,
  subtitle,
  draftKey,
  draftSnapshot,
  stepErrors = {},
  validateStep,
  onOuterBack,
  onFinalSubmit,
  isSubmitting = false,
  steps = FORM_WIZARD_STEPS,
  renderDetails,
  renderProperty,
  renderDocuments,
  renderReview,
  detailsContinueDisabled = false,
}) {
  const [step, setStep] = useState(0);
  const reviewSubmitRef = useRef(() => {});
  const [reviewCanSubmit, setReviewCanSubmit] = useState(false);
  const totalSteps = steps.length;
  const current = steps[step] || steps[0];
  const isReview = current?.id === "review";

  useBorrowerFormDraft(draftKey, draftSnapshot, Boolean(draftKey));

  const goToStep = useCallback((idx) => {
    setStep(Math.max(0, Math.min(idx, totalSteps - 1)));
  }, [totalSteps]);

  const handleReviewFooterState = useCallback((state) => {
    reviewSubmitRef.current = state?.onSubmit || (() => {});
    setReviewCanSubmit((prev) => (prev === Boolean(state?.canSubmit) ? prev : Boolean(state?.canSubmit)));
  }, []);

  const handleBack = () => {
    if (step > 0) {
      goToStep(step - 1);
      return;
    }
    onOuterBack?.();
  };

  const handleContinue = async () => {
    if (isReview) return;
    const ok = validateStep ? await validateStep(step, current?.id) : true;
    if (!ok) return;
    if (draftKey && draftSnapshot) saveFormDraft(draftKey, draftSnapshot);
    goToStep(step + 1);
  };

  const nextIsReview = steps[step + 1]?.id === "review";
  const primaryLabel = nextIsReview ? "Next" : isReview ? "Submit Application" : "Continue";

  const renderCurrent = () => {
    const reviewProps = { goToStep, onFooterStateChange: handleReviewFooterState };
    switch (current?.id) {
      case "details":
        return renderDetails?.();
      case "property":
        return renderProperty?.();
      case "documents":
        return renderDocuments?.();
      case "review":
        return renderReview?.(reviewProps);
      default:
        return null;
    }
  };

  return (
    <div className="pb-2 min-w-0">
      <div className="bg-sky-400 text-white rounded-t-2xl -mx-4 -mt-4 md:-mx-6 md:-mt-6 px-4 py-4 md:px-6 md:py-5">
        <div className="min-w-0">
          <h2 className="text-lg sm:text-xl font-extrabold tracking-tight leading-tight">{title}</h2>
          {subtitle ? (
            <p className="mt-1 text-xs sm:text-sm opacity-95 leading-snug">{subtitle}</p>
          ) : null}
        </div>
      </div>

      <div className="pt-4">
        <FormStepProgress currentStep={step} stepErrors={stepErrors} steps={steps} />
      </div>

      <div className={`space-y-6 min-w-0 ${isReview ? "pb-24" : ""}`}>
        {renderCurrent()}
      </div>

      <FormStickyFooter
        onBack={handleBack}
        onPrimary={
          isReview
            ? () => reviewSubmitRef.current?.()
            : handleContinue
        }
        primaryLabel={isReview ? (isSubmitting ? "Submitting…" : "Submit Application") : primaryLabel}
        primaryDisabled={isReview ? !reviewCanSubmit : current?.id === "details" && detailsContinueDisabled}
        primaryLoading={isReview && isSubmitting}
        backLabel={step === 0 ? "← Back" : "Back"}
      />
    </div>
  );
}

export { FORM_WIZARD_STEPS };
